import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from "react-i18next";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await apiRequest("POST", "/api/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message || t("authPages.genericError"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-30 -z-10" />

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md space-y-8"
      >
        <div className="space-y-6">
          <div className="h-16 w-16 bg-primary rounded-2xl flex items-center justify-center shadow-xl shadow-primary/20 rotate-3">
            <Building2 className="h-8 w-8 text-primary-foreground" />
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">
              {t("authPages.resetTitle")}
            </h1>
            <p className="text-muted-foreground">
              {t("authPages.resetSubtitle")}
            </p>
          </div>
        </div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 rounded-xl p-6 space-y-4"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <h2 className="text-lg font-semibold text-green-900">{t("authPages.checkEmailTitle")}</h2>
            </div>
            <p className="text-green-800">
              {t("authPages.checkEmailDesc")}
            </p>
            <p className="text-sm text-green-700">
              {t("authPages.linkExpire")}
            </p>
            <Link href="/login">
              <Button variant="outline" className="w-full mt-4" data-testid="button-back-to-login">
                <ArrowLeft className="h-4 w-4 me-2" />
                {t("authPages.backToLogin")}
              </Button>
            </Link>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ps-1">
                  {t("authPages.emailLabel")}
                </Label>
                <div className="relative group">
                  <Mail className="absolute start-3 top-3 h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <Input 
                    type="email" 
                    placeholder={t("authPages.forgotEmailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 ps-10 bg-background border-muted-foreground/20 hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all rounded-lg"
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full h-12 text-base font-medium bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-primary text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 rounded-lg"
              data-testid="button-send-reset-link"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("authPages.sending")}
                </span>
              ) : (
                t("authPages.sendResetLink")
              )}
            </Button>
            
            <div className="text-center">
              <Link href="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" />
                {t("authPages.backToLogin")}
              </Link>
            </div>
          </form>
        )}

        <div className="pt-6 flex items-center justify-between border-t border-border/40">
          <p className="text-xs text-muted-foreground font-medium">
            {t("authPages.securityLabel")}
          </p>
          <div className="flex gap-4">
            <div className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
            <span className="text-xs text-muted-foreground font-mono">{t("authPages.systemOnlineCaps")}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
